import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type {
	GetCurrentDashaResponse,
	GetMajorDashasResponse,
	GetSubDashasResponse,
} from '../types/index.js';
import { RoxyDataElement } from '../utils/base-element.js';
import { baseStyles } from '../utils/base-styles.js';
import { disclosureStyles } from '../utils/disclosure.js';
import { formatDate, formatNumber } from '../utils/format.js';
import {
	type InterpSection,
	interpAccordionStyles,
	renderInterpAccordion,
} from '../utils/interp-accordion.js';

type DashaData =
	| GetCurrentDashaResponse
	| GetMajorDashasResponse
	| GetSubDashasResponse;

type DashaPeriod = GetMajorDashasResponse['mahadashas'][number];
type Remaining = GetCurrentDashaResponse['remainingInMahadasha'];

/** The level a period sits at, so no view ever labels an antardasha a mahadasha. */
const LEVEL_LABEL = {
	current: 'Mahadasha',
	major: 'Mahadasha',
	sub: 'Antardasha',
} as const;

/** "8y 11m 5d", the form a dasha reader expects for a remaining balance. */
function formatBalance(b: Remaining | undefined): string {
	if (!b) return '';
	const parts: string[] = [];
	if (b.years) parts.push(`${b.years}y`);
	if (b.months) parts.push(`${b.months}m`);
	if (b.days) parts.push(`${b.days}d`);
	return parts.length ? parts.join(' ') : '0d';
}

/** "Jun 17, 2018 to Jun 17, 2035" for the accordion aside. */
function formatSpan(p: { startDate?: string; endDate?: string }): string {
	const start = p.startDate ? formatDate(p.startDate) : '';
	const end = p.endDate ? formatDate(p.endDate) : '';
	if (start && end) return `${start} to ${end}`;
	return start || end;
}

/**
 * Dasha timeline. Renders /vedic-astrology/dasha/{current,major,sub/{...}}.
 *
 * @remarks
 * Default mode shows the active mahadasha, antardasha and pratyantardasha with
 * the reading each one carries. `period="major"` draws the full 120-year
 * Vimshottari timeline; `period="sub"` draws the antardashas inside one
 * mahadasha and states which mahadasha they sit in, since an antardasha means
 * nothing without its parent lord.
 */
@customElement('roxy-dasha-timeline')
export class RoxyDashaTimeline extends RoxyDataElement<DashaData> {
	static styles = [
		baseStyles,
		disclosureStyles,
		interpAccordionStyles,
		css`.wrap{gap:var(--roxy-space-md,1rem);display:grid}.head{justify-content:space-between;align-items:center;gap:var(--roxy-space-sm,.5rem);flex-wrap:wrap;display:flex}.title{font-size:var(--roxy-text-lg,1.125rem);font-weight:var(--roxy-weight-bold,600);margin:0}.nakshatra{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-sm,.875rem)}.current{gap:var(--roxy-space-md,1rem);background:var(--roxy-surface,#fff);border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);padding:var(--roxy-space-md,1rem);box-shadow:var(--roxy-shadow-sm);grid-template-columns:repeat(auto-fit,minmax(10rem,1fr));display:grid}.current div span:first-child{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);text-transform:uppercase;letter-spacing:.06em;display:block}.current div strong{font-size:var(--roxy-text-base,1rem);color:var(--roxy-fg,#0a0a0a)}.current div small{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);display:block}.balance,.parent{font-size:var(--roxy-text-sm,.875rem);color:var(--roxy-muted,#71717a);border-left:2px solid var(--roxy-border,#e4e4e7);padding-left:var(--roxy-space-sm,.5rem);margin:0}.timeline{gap:var(--roxy-space-xs,.25rem);display:grid}.bar{gap:var(--roxy-space-sm,.5rem);font-size:var(--roxy-text-sm,.875rem);grid-template-columns:5rem 1fr 8rem;align-items:center;display:grid}.bar.now strong{color:var(--roxy-accent-ink,#b45309)}.now-badge{font-size:var(--roxy-text-xs,.75rem);font-weight:var(--roxy-weight-bold,600);color:var(--roxy-accent-ink,#b45309);text-transform:uppercase;letter-spacing:.06em;margin-left:.4em;display:inline-block}.bar-track{background:var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-full,9999px);height:14px;position:relative;overflow:hidden}.bar-fill{background:var(--roxy-accent,#f59e0b);opacity:.45;height:100%;transition:width var(--roxy-motion-duration,.2s) var(--roxy-motion-easing,cubic-bezier(.4, 0, .2, 1));display:block}.bar-now .bar-fill{opacity:1}.bar-progress{background:var(--roxy-accent-ink,#b45309);width:2px;box-shadow:0 0 0 2px color-mix(in srgb, var(--roxy-accent,#f59e0b) 35%, transparent);border-radius:2px;position:absolute;top:-2px;bottom:-2px}.dates{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);font-variant-numeric:tabular-nums;text-align:right}.block{border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);background:var(--roxy-surface,#fff);padding:var(--roxy-space-md,1rem)}`,
	];

	@property({ type: String, reflect: true })
	period: 'current' | 'major' | 'sub' = 'current';

	protected renderEmpty() {
		return html`<div class="roxy-empty" role="status">No dasha data</div>`;
	}

	protected renderData(d: DashaData) {
		const periods = this.collectPeriods(d);
		const maxYears = periods.length
			? Math.max(...periods.map((p) => p.durationYears))
			: 0;

		return html`<div class="wrap" aria-label="Dasha timeline"><header class="head"><h2 class="title">${this.heading(d)}</h2>${
					'nakshatraName' in d && d.nakshatraName
						? html`<div class="nakshatra">Moon nakshatra: ${d.nakshatraName} ${'nakshatraLord' in d && d.nakshatraLord ? html`(lord ${d.nakshatraLord})` : nothing}</div>`
						: nothing
				}</header>${this.renderBirthBalance(d)} ${this.renderParentMahadasha(d)} ${this.period === 'current' ? this.renderCurrent(d) : nothing} ${
				periods.length > 0
					? html`<div class="timeline" role="list">${periods.map((p) => this.renderBar(p, maxYears))}</div>`
					: nothing
			} ${renderInterpAccordion(this.readings(d, periods), 'roxy-dasha', 'Reading')}</div>`;
	}

	private heading(d: DashaData): string {
		if (this.period === 'major') return 'Vimshottari Mahadasha';
		if (this.period === 'sub') {
			const lord = 'mahadashaLord' in d ? d.mahadashaLord : '';
			return lord ? `Antardashas in ${lord} Mahadasha` : 'Antardashas';
		}
		return 'Active dashas';
	}

	private renderBirthBalance(d: DashaData) {
		if (!('birthDashaBalance' in d) || !d.birthDashaBalance) return nothing;
		const lord = 'nakshatraLord' in d && d.nakshatraLord ? d.nakshatraLord : '';
		const remaining = formatBalance(d.birthDashaBalance);
		return html`<p class="balance">Birth dasha balance: ${remaining} of ${lord ? html`<strong>${lord}</strong>` : 'the opening mahadasha'} remained at birth.</p>`;
	}

	/** Sub mode lists antardashas inside one mahadasha, so name the parent and its span. */
	private renderParentMahadasha(d: DashaData) {
		if (!('mahadashaPeriod' in d) || !d.mahadashaPeriod) return nothing;
		const p = d.mahadashaPeriod;
		const span = formatSpan(p);
		return html`<p class="parent">Inside the <strong>${p.planet}</strong> Mahadasha${span ? `, ${span}` : ''} ${typeof p.durationYears === 'number' ? `(${formatNumber(p.durationYears, 1)} years)` : ''}.</p>`;
	}

	/**
	 * Every reading the response carries, behind one exclusive accordion. Current
	 * mode has three (mahadasha, antardasha, pratyantardasha); sub mode leads with
	 * the parent mahadasha reading, then the antardasha running now; major mode
	 * shows the mahadasha running now.
	 */
	private readings(d: DashaData, periods: DashaPeriod[]): InterpSection[] {
		const sections: InterpSection[] = [];

		if ('mahadashaPeriod' in d && d.mahadashaPeriod?.interpretation) {
			sections.push({
				label: `${d.mahadashaPeriod.planet} Mahadasha`,
				aside: formatSpan(d.mahadashaPeriod),
				body: d.mahadashaPeriod.interpretation,
			});
		}

		if ('mahadasha' in d) {
			const levels = [
				['Mahadasha', d.mahadasha, d.remainingInMahadasha],
				['Antardasha', d.antardasha, d.remainingInAntardasha],
				['Pratyantardasha', d.pratyantardasha, d.remainingInPratyantardasha],
			] as const;
			for (const [label, period, remaining] of levels) {
				if (!period?.interpretation) continue;
				const left = formatBalance(remaining);
				sections.push({
					label: `${period.planet} ${label}`,
					aside: left ? `${left} left` : formatSpan(period),
					body: period.interpretation,
				});
			}
		}

		const active = periods.find((p) => this.isCurrent(p));
		if (active?.interpretation) {
			sections.push({
				label: `${active.planet} ${LEVEL_LABEL[this.period]}`,
				aside: formatSpan(active),
				body: active.interpretation,
			});
		}

		return sections;
	}

	private renderCurrent(d: DashaData) {
		if (!('mahadasha' in d)) return nothing;
		const levels = [
			['Mahadasha', d.mahadasha, d.remainingInMahadasha],
			['Antardasha', d.antardasha, d.remainingInAntardasha],
			['Pratyantardasha', d.pratyantardasha, d.remainingInPratyantardasha],
		] as const;
		return html`<div class="current">${levels.map(([label, period, remaining]) => {
				if (!period) return nothing;
				const left = formatBalance(remaining);
				return html`<div><span>${label}</span> <strong>${period.planet}</strong> ${left ? html`<small>${left} left</small>` : nothing}</div>`;
			})}</div>`;
	}

	private collectPeriods(d: DashaData): DashaPeriod[] {
		if ('mahadashas' in d && d.mahadashas?.length) return d.mahadashas;
		if ('antardashas' in d && d.antardashas?.length) return d.antardashas;
		return [];
	}

	/** True when the current wall-clock time falls between the period's start and end. */
	private isCurrent(p: DashaPeriod): boolean {
		if (!p.startDate || !p.endDate) return false;
		const now = Date.now();
		const start = Date.parse(p.startDate);
		const end = Date.parse(p.endDate);
		if (Number.isNaN(start) || Number.isNaN(end)) return false;
		return now >= start && now < end;
	}

	/**
	 * Fractional progress (0..1) through a period at the current time. Used to
	 * draw a vertical "now" marker inside the active bar. Returns -1 outside the
	 * period so the caller can skip the marker.
	 */
	private progressIn(p: DashaPeriod): number {
		if (!p.startDate || !p.endDate) return -1;
		const start = Date.parse(p.startDate);
		const end = Date.parse(p.endDate);
		const now = Date.now();
		if (
			Number.isNaN(start) ||
			Number.isNaN(end) ||
			now < start ||
			now >= end ||
			end <= start
		) {
			return -1;
		}
		return (now - start) / (end - start);
	}

	private renderBar(p: DashaPeriod, max: number) {
		const years = p.durationYears;
		const width = max > 0 ? (years / max) * 100 : 0;
		const current = this.isCurrent(p);
		const progress = current ? this.progressIn(p) : -1;
		const trackClass = current ? 'bar-track bar-now' : 'bar-track';
		return html`<div class="${current ? 'bar now' : 'bar'}" role="listitem" aria-current="${current ? 'time' : 'false'}"><span><strong>${p.planet}</strong>${current ? html`<span class="now-badge">Now</span>` : nothing} </span><span class="${trackClass}"><span class="bar-fill" style="width:${width}%"></span> ${
					progress >= 0
						? html`<span class="bar-progress" style="left:${progress * width}%" aria-hidden="true"></span>`
						: nothing
				} </span><span class="dates">${p.startDate ? formatYear(p.startDate) : ''} ${p.endDate ? html`- ${formatYear(p.endDate)}` : ''}</span></div>`;
	}
}

function formatYear(s: string): string {
	const m = s.match(/^(\d{4})/);
	return m ? m[1] : s;
}

declare global {
	interface HTMLElementTagNameMap {
		'roxy-dasha-timeline': RoxyDashaTimeline;
	}
}
